import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import Icon from '../ui/Icon'

export type DropdownMenuItem = {
  key: string
  label: string
  icon?: string
  danger?: boolean
  disabled?: boolean
  onClick: () => void
}

type DropdownMenuProps = {
  label: string
  items: DropdownMenuItem[]
  disabled?: boolean
  trigger?: ReactNode
}

const MENU_OFFSET = 4

/**
 * Icon-button menu rendered in a portal with fixed positioning, so it is not clipped by scrolling containers
 * such as table wrappers. Opens below the trigger, or above it when there is not enough room.
 */
export const DropdownMenu = ({
  label,
  items,
  disabled = false,
  trigger,
}: DropdownMenuProps) => {
  const [open, setOpen] = useState(false)
  const [style, setStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => setOpen(false), [])

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) {
      return
    }

    const rect = triggerRef.current.getBoundingClientRect()
    const menuHeight = menuRef.current.offsetHeight
    const fitsBelow =
      rect.bottom + MENU_OFFSET + menuHeight <= window.innerHeight

    setStyle({
      right: window.innerWidth - rect.right,
      ...(fitsBelow
        ? { top: rect.bottom + MENU_OFFSET }
        : { bottom: window.innerHeight - rect.top + MENU_OFFSET }),
    })
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        close()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)
    // The menu is positioned once; close it instead of following the trigger.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open, close])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-ghost btn-sm btn-icon ph-dropdown-menu-trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={event => {
          event.stopPropagation()
          setOpen(value => !value)
        }}
      >
        {trigger ?? <Icon name="more" size={15} />}
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="ph-dropdown-menu"
            role="menu"
            style={style}
          >
            {items.map(item => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={`ph-dropdown-menu-item${item.danger ? ' danger' : ''}`}
                disabled={item.disabled}
                onClick={() => {
                  close()
                  item.onClick()
                }}
              >
                {item.icon && <Icon name={item.icon} size={14} />}
                <span>{item.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  )
}
